import { supabase } from '@/lib/supabase';
import { handleError, ErrorSeverity, ErrorContext } from '@/utils/errorHandler';
import { EmailService } from '@/services/emailService';
import { 
  DocumentRequest, 
  DocumentRequestWithStats,
  DocumentRequestFamily,
  DocumentSubmission,
  CreateDocumentRequestData,
  UpdateDocumentRequestData,
  DocumentRequestFilters,
  DocumentRequestListResponse,
  DocumentRequestDetailsResponse,
  ParentDocumentRequest,
  DocumentRequestStats
} from '@/types/documentRequestTypes';

export const DocumentRequestService = {
  /**
   * Create a new document request
   */
  async createDocumentRequest(data: CreateDocumentRequestData): Promise<{ success: boolean; error: string | null; data?: DocumentRequest }> {
    const context: ErrorContext = { 
      operation: 'createDocumentRequest',
      additionalInfo: { data }
    };

    try {
      // Create the document request
      const { data: request, error: requestError } = await supabase
        .from('document_requests')
        .insert({
          title: data.title,
          description: data.description,
          deadline: data.deadline,
          organization_id: data.organization_id,
          created_by: data.created_by,
          status: 'draft',
          signing_mode: data.signing_mode,
          template_id: data.template_id
        })
        .select()
        .single();

      if (requestError) throw requestError;

      // Add families to the request
      if (data.family_ids.length > 0) {
        const familyInserts = data.family_ids.map(family_id => ({
          request_id: request.id,
          family_id,
          status: 'pending' as const
        }));

        const { error: familyError } = await supabase
          .from('document_request_families')
          .insert(familyInserts);

        if (familyError) throw familyError;
      }

      // If this is a form-based request, create signing sessions immediately
      if (data.signing_mode === 'form' && data.template_id) {
        try {
          // Create signing sessions for each family
          const signingSessionPromises = data.family_ids.map(async (family_id) => {
            // Get family email - we need primary parent email
            const { data: parentData } = await supabase
              .from('parents')
              .select(`
                user_profiles!inner(email)
              `)
              .eq('family_id', family_id)
              .eq('is_primary', true)
              .limit(1)
              .single();

            if (parentData?.user_profiles?.email) {
              // Create signing session record
              await supabase
                .from('signing_sessions')
                .insert({
                  request_id: request.id,
                  family_id: family_id,
                  parent_email: parentData.user_profiles.email,
                  status: 'pending'
                });
            }
          });

          await Promise.all(signingSessionPromises);
        } catch (error) {
          console.error('Error creating signing sessions:', error);
          // Continue even if signing sessions fail
        }
      }

      return { success: true, error: null, data: request };
    } catch (error) {
      handleError(error, "Failed to create document request", ErrorSeverity.ERROR, context);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  },

  /**
   * Get document requests with filtering and pagination
   */
  async getDocumentRequests(
    organizationId: string, 
    filters: DocumentRequestFilters = {},
    page: number = 1,
    perPage: number = 20
  ): Promise<{ success: boolean; error: string | null; data?: DocumentRequestListResponse }> {
    const context: ErrorContext = { 
      operation: 'getDocumentRequests',
      additionalInfo: { organizationId, filters, page, perPage }
    };

    try {
      let query = supabase
        .from('document_requests')
        .select(`
          *,
          created_by_profile:user_profiles!document_requests_created_by_fkey(first_name, last_name)
        `, { count: 'exact' })
        .eq('organization_id', organizationId);

      // Apply filters
      if (filters.status && filters.status.length > 0) {
        query = query.in('status', filters.status);
      }

      if (filters.deadline_from) {
        query = query.gte('deadline', filters.deadline_from);
      }

      if (filters.deadline_to) {
        query = query.lte('deadline', filters.deadline_to);
      }

      if (filters.search_query) {
        query = query.or(`title.ilike.%${filters.search_query}%,description.ilike.%${filters.search_query}%`);
      }

      if (filters.created_by) {
        query = query.eq('created_by', filters.created_by);
      }

      // Add pagination
      const from = (page - 1) * perPage;
      query = query.range(from, from + perPage - 1);

      // Order by created_at desc
      query = query.order('created_at', { ascending: false });

      const { data: requests, error: requestsError, count } = await query;

      if (requestsError) throw requestsError;

      // Get stats for each request
      const requestsWithStats: DocumentRequestWithStats[] = [];
      for (const request of requests || []) {
        const stats = await this.getDocumentRequestStats(request.id);
        requestsWithStats.push({
          ...request,
          stats: stats.data || {
            total_families: 0,
            submitted_count: 0,
            pending_count: 0,
            approved_count: 0,
            rejected_count: 0,
            completion_percentage: 0
          },
          total_families: stats.data?.total_families || 0,
          submitted_count: stats.data?.submitted_count || 0,
          pending_count: stats.data?.pending_count || 0
        });
      }

      const totalPages = Math.ceil((count || 0) / perPage);

      return {
        success: true,
        error: null,
        data: {
          data: requestsWithStats,
          count: count || 0,
          page,
          per_page: perPage,
          total_pages: totalPages
        }
      };
    } catch (error) {
      handleError(error, "Failed to get document requests", ErrorSeverity.ERROR, context);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  },

  /**
   * Get document request details with families and submissions
   */
  async getDocumentRequestDetails(requestId: string): Promise<{ success: boolean; error: string | null; data?: DocumentRequestDetailsResponse }> {
    const context: ErrorContext = { 
      operation: 'getDocumentRequestDetails',
      additionalInfo: { requestId }
    };

    try {
      // Get the request
      const { data: request, error: requestError } = await supabase
        .from('document_requests')
        .select(`
          *,
          created_by_profile:user_profiles!document_requests_created_by_fkey(first_name, last_name)
        `)
        .eq('id', requestId)
        .single();

      if (requestError) throw requestError;

      // Get families assigned to this request
      const { data: families, error: familiesError } = await supabase
        .from('document_request_families')
        .select(`
          *,
          family:families(id, name)
        `)
        .eq('request_id', requestId)
        .order('created_at');

      if (familiesError) throw familiesError;

      // Get submissions for this request
      const { data: submissions, error: submissionsError } = await supabase
        .from('document_submissions')
        .select(`
          *,
          family:families(id, name),
          reviewer:user_profiles!document_submissions_reviewed_by_fkey(first_name, last_name)
        `)
        .eq('request_id', requestId)
        .order('submitted_at', { ascending: false });

      if (submissionsError) throw submissionsError;

      // Get reminders for this request
      const { data: reminders, error: remindersError } = await supabase
        .from('request_reminders')
        .select('*')
        .eq('request_id', requestId)
        .order('sent_at', { ascending: false });

      if (remindersError) throw remindersError;

      // Get stats
      const stats = await this.getDocumentRequestStats(requestId);

      const requestWithStats: DocumentRequestWithStats = {
        ...request,
        stats: stats.data || {
          total_families: 0,
          submitted_count: 0,
          pending_count: 0,
          approved_count: 0,
          rejected_count: 0,
          completion_percentage: 0
        },
        total_families: stats.data?.total_families || 0,
        submitted_count: stats.data?.submitted_count || 0,
        pending_count: stats.data?.pending_count || 0
      };

      return {
        success: true,
        error: null,
        data: {
          request: requestWithStats,
          families: families || [],
          submissions: submissions || [],
          reminders: reminders || []
        }
      };
    } catch (error) {
      handleError(error, "Failed to get document request details", ErrorSeverity.ERROR, context);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  },

  /**
   * Update a document request
   */
  async updateDocumentRequest(requestId: string, updates: UpdateDocumentRequestData): Promise<{ success: boolean; error: string | null; data?: DocumentRequest }> {
    const context: ErrorContext = { 
      operation: 'updateDocumentRequest',
      additionalInfo: { requestId, updates }
    };

    try {
      const { data, error } = await supabase
        .from('document_requests')
        .update(updates)
        .eq('id', requestId)
        .select()
        .single();

      if (error) throw error;

      return { success: true, error: null, data };
    } catch (error) {
      handleError(error, "Failed to update document request", ErrorSeverity.ERROR, context);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  },

  /**
   * Send document request emails to families
   */
  async sendDocumentRequest(requestId: string): Promise<{ success: boolean; error: string | null; sentCount?: number }> {
    const context: ErrorContext = { 
      operation: 'sendDocumentRequest',
      additionalInfo: { requestId }
    };

    try {
      // Get request details
      const requestDetails = await this.getDocumentRequestDetails(requestId);
      if (!requestDetails.success || !requestDetails.data) {
        throw new Error('Failed to get request details');
      }

      const { request, families } = requestDetails.data;

      // Get organization details
      const { data: organization, error: orgError } = await supabase
        .from('organizations')
        .select('name')
        .eq('id', request.organization_id)
        .single();

      if (orgError) throw orgError;

      let sentCount = 0;
      const errors: string[] = [];

      // Send emails to each family
      for (const family of families) {
        if (family.status === 'pending' && family.family) {
          try {
            // Get family's primary parent email
            // Note: We'll need to get email from user_profiles or use parent invitations table
            const { data: parentInvitations, error: inviteError } = await supabase
              .from('parent_invitations')
              .select('email')
              .eq('family_id', family.family_id)
              .limit(1);

            let parentEmail: string | null = null;
            
            if (parentInvitations && parentInvitations.length > 0) {
              parentEmail = parentInvitations[0].email;
            } else {
              // Try to get from parents table with user profiles
              const { data: parents, error: parentsError } = await supabase
                .from('parents')
                .select(`
                  user_id,
                  user_profiles!inner(email)
                `)
                .eq('family_id', family.family_id)
                .eq('is_primary', true)
                .limit(1);

              if (!parentsError && parents && parents.length > 0) {
                parentEmail = parents[0].user_profiles?.email;
              }
            }

            if (!parentEmail) {
              errors.push(`No email found for family ${family.family?.name}`);
              continue;
            }

            // Construct upload link
            const siteUrl = import.meta.env.VITE_SITE_URL || "https://www.hivetivity.com";
            const uploadLink = `${siteUrl}/parent-portal/documents?request=${requestId}`;

            // Get children names for this family
            const { data: students } = await supabase
              .from('students')
              .select('first_name')
              .eq('family_id', family.family_id);

            const emailData: DocumentRequestEmailData = {
              request_title: request.title,
              request_description: request.description,
              deadline: request.deadline,
              family_name: family.family.name,
              children_names: students?.map(s => s.first_name) || [],
              upload_link: uploadLink,
              organization_name: organization.name
            };

            // Send email using existing email service
            const emailResult = await EmailService.sendDocumentRequestEmail(
              parentEmail,
              family.family.name,
              request.title,
              request.description,
              request.deadline,
              students?.map(s => s.first_name) || [],
              uploadLink,
              organization.name,
              undefined, // organizationLogoUrl
              request.signing_mode
            );

            if (!emailResult.success) {
              throw new Error(emailResult.error || 'Failed to send email');
            }

            // Record reminder
            await supabase
              .from('request_reminders')
              .insert({
                request_id: requestId,
                family_id: family.family_id,
                reminder_type: 'initial'
              });

            sentCount++;
          } catch (emailError) {
            errors.push(`Failed to send to ${family.family?.name}: ${emailError instanceof Error ? emailError.message : 'Unknown error'}`);
          }
        }
      }

      // Update request status to active
      await this.updateDocumentRequest(requestId, { status: 'active' });

      const hasErrors = errors.length > 0;
      return {
        success: !hasErrors || sentCount > 0,
        error: hasErrors ? `Some emails failed: ${errors.join(', ')}` : null,
        sentCount
      };
    } catch (error) {
      handleError(error, "Failed to send document request", ErrorSeverity.ERROR, context);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  },

  /**
   * Get document request statistics
   */
  async getDocumentRequestStats(requestId: string): Promise<{ success: boolean; error: string | null; data?: DocumentRequestStats }> {
    try {
      const { data, error } = await supabase
        .rpc('get_document_request_stats', { request_uuid: requestId });

      if (error) throw error;

      const stats = data?.[0] || {
        total_families: 0,
        submitted_count: 0,
        pending_count: 0,
        approved_count: 0,
        rejected_count: 0,
        completion_percentage: 0
      };

      return { success: true, error: null, data: stats };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  },

  /**
   * Submit a document for a request (parent-facing)
   */
  async submitDocument(
    requestId: string, 
    familyId: string, 
    file: File
  ): Promise<{ success: boolean; error: string | null; data?: DocumentSubmission }> {
    const context: ErrorContext = { 
      operation: 'submitDocument',
      additionalInfo: { requestId, familyId, fileName: file.name }
    };

    try {
      // Validate file
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        return { success: false, error: 'File size must be less than 10MB' };
      }

      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        return { success: false, error: 'Only PDF and image files are allowed' };
      }

      // Get organization ID from the document request
      const { data: request, error: requestError } = await supabase
        .from('document_requests')
        .select('organization_id')
        .eq('id', requestId)
        .single();

      if (requestError) throw requestError;
      if (!request) throw new Error('Document request not found');

      // Upload file to parent-documents bucket with organized path
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `${request.organization_id}/requests/${requestId}/${familyId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('parent-documents')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Create submission record
      const { data: submission, error: submissionError } = await supabase
        .from('document_submissions')
        .insert({
          request_id: requestId,
          family_id: familyId,
          file_name: file.name,
          file_path: filePath,
          file_size: file.size,
          file_type: file.type
        })
        .select()
        .single();

      if (submissionError) throw submissionError;

      return { success: true, error: null, data: submission };
    } catch (error) {
      handleError(error, "Failed to submit document", ErrorSeverity.ERROR, context);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  },

  /**
   * Get parent's document requests
   */
  async getParentDocumentRequests(familyId: string): Promise<{ success: boolean; error: string | null; data?: ParentDocumentRequest[] }> {
    const context: ErrorContext = { 
      operation: 'getParentDocumentRequests',
      additionalInfo: { familyId }
    };

    try {
      // Get all document requests assigned to this family
      const { data: familyRequests, error: familyRequestsError } = await supabase
        .from('document_request_families')
        .select(`
          request_id,
          status
        `)
        .eq('family_id', familyId);

      if (familyRequestsError) throw familyRequestsError;

      if (!familyRequests || familyRequests.length === 0) {
        return { success: true, error: null, data: [] };
      }

      // Get unique request IDs
      const requestIds = [...new Set(familyRequests.map(r => r.request_id))];

      // Get the actual document requests
      const { data: requests, error: requestsError } = await supabase
        .from('document_requests')
        .select('*')
        .in('id', requestIds);

      if (requestsError) throw requestsError;

      // Get all submissions for this family
      const { data: submissions, error: submissionsError } = await supabase
        .from('document_submissions')
        .select('*')
        .eq('family_id', familyId)
        .in('request_id', requestIds);

      if (submissionsError) throw submissionsError;

      // Get organization name
      const organizationId = requests?.[0]?.organization_id;
      let organizationName = 'Your Organization';
      
      if (organizationId) {
        const { data: org } = await supabase
          .from('organizations')
          .select('name')
          .eq('id', organizationId)
          .single();
        
        organizationName = org?.name || organizationName;
      }

      // Map requests to parent document request format
      const parentRequests: ParentDocumentRequest[] = (requests || []).map(request => {
        // Find submissions for this request
        const requestSubmissions = submissions?.filter(s => s.request_id === request.id) || [];
        
        // Determine status based on submissions
        let status: FamilyRequestStatus = 'pending';
        if (requestSubmissions.length > 0) {
          status = 'submitted';
          // Check if any submission is approved/rejected
          const hasApproved = requestSubmissions.some(s => s.reviewed_at && s.notes?.includes('approved'));
          const hasRejected = requestSubmissions.some(s => s.reviewed_at && s.notes?.includes('rejected'));
          if (hasApproved) status = 'approved';
          else if (hasRejected) status = 'rejected';
        }

        return {
          id: request.id,
          title: request.title,
          description: request.description,
          deadline: request.deadline,
          status: status,
          submitted_at: requestSubmissions[0]?.submitted_at,
          family_id: familyId,
          organization_name: organizationName,
          submissions: requestSubmissions,
          can_submit: status === 'pending' && (!request.deadline || new Date(request.deadline) > new Date()),
          signing_mode: request.signing_mode,
          template_id: request.template_id
        };
      });

      return { success: true, error: null, data: parentRequests };
    } catch (error) {
      handleError(error, "Failed to get parent document requests", ErrorSeverity.ERROR, context);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  },

};